import React, { createContext, useContext } from 'react';
import { successToast, errorToast } from './Toasts';

export const HttpRequestContext = createContext({
    sendRequest: () => {},
    sendRequestWithToast: () => {},
    httpRequest: () => {},
});

export function getBaseDomain(url) {
    const hostname = (typeof url === 'string') ? (new URL(url)).hostname : window.location.hostname;
    
    // Handle any subdomain by extracting the base domain
    if (hostname.includes('innovaplus.be')) {
        // Extract innovaplus.be (or innovaplus.com, etc.) from any subdomain
        return 'innovaplus.be';
    } else if (hostname.includes('localhost')) {
        // For localhost, always return 'localhost' regardless of subdomain
        return 'localhost';
    }
    
    // Fallback: return hostname as-is for other domains
    return hostname;
}

export const HttpRequestProvider = ({ children }) => {

    const sendRequest = async ({ apiPath, type, request, headers, responseType }) => {
        const baseDomain = getBaseDomain();
    
        if (apiPath.startsWith('/')) {
            apiPath = apiPath.slice(1);
        }
        if (apiPath.startsWith('api/')) {
            apiPath = apiPath.slice(4);
        }
    
        let apiUrl = '';
        if (! apiPath.startsWith('blob:')) {
            apiUrl = `//${baseDomain}/api/${apiPath}`; 
        } else {
            apiUrl = apiPath;
        }


        headers = { 
            ...{ 'Content-Type': 'application/json; charset=utf-8' }, 
            ...headers 
        };
    
        // Only process request body for non-GET/HEAD methods
        let requestBody = undefined;
        if (type !== 'GET' && type !== 'HEAD' && request) {
            if (!(request instanceof FormData)) {
                requestBody = JSON.stringify(request);
            } else {
                delete headers['Content-Type']; // Let the browser set the correct Content-Type for FormData
                requestBody = request;
            }
        }
    
        try {
            const response = await fetch(apiUrl, {
                method: type,
                headers: headers,
                body: requestBody, // Will be undefined for GET/HEAD
                credentials: 'include'
            });
    
            if (!response.ok) {
                // Traiter la réponse comme JSON pour extraire le message d'erreur
                const errorData = await response.json();
                const serverMessage = errorData && errorData.message ? errorData.message : "Error occurred";
                throw new Error(serverMessage);
            }
    
            // If responseType is explicitly set to 'blob', return as blob
            if (responseType === 'blob') {
                return await response.blob();
            }
            
            // Otherwise, check content type and determine response format
            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.includes('application/json')) {
                // Traiter la réponse comme JSON
                return await response.json();
            } else {
                // Traiter la réponse comme un blob binaire si ce n'est pas du JSON
                return await response.blob();
            }
        } catch (error) {
            throw error;
        }
    };

    // Simplified helper function for direct http requests
    const httpRequest = async (method, path, data = null, responseType = null) => {
        return sendRequest({
            apiPath: path,
            type: method,
            request: data,
            responseType: responseType
        });
    };
    
    const sendRequestWithToast = async ({
        apiPath,
        request,
        type,
        successMessage,
        errorMessage,
        successRedirectUrl,
        needRecaptcha,
        recaptchaRef
    }) => {
        // Check for reCAPTCHA
        if (needRecaptcha && (!request.recaptchaResponse || request.recaptchaResponse.length === 0)) {
            errorToast("Merci de prouver que vous n'êtes pas un robot.");
            return;
        }

        // Define headers
        const headers = {
            "Content-Type": "application/json; charset=utf-8",
            "time-zone": Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        try {
            const responseData = await sendRequest({ apiPath, type, request, headers });
            successToast(successMessage);
    
            if (successRedirectUrl) {
                window.location.href = successRedirectUrl;
            }
            return responseData;  // Return true for a successful request
        } catch (error) {
            errorToast(error.message || errorMessage);
    
            if (needRecaptcha && recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            console.error("Error occurred:", error);
            return false;  // Return false for a failed request
        }
    }

    return (
        <HttpRequestContext.Provider value={{ sendRequest, sendRequestWithToast, httpRequest }}>
            {children}
        </HttpRequestContext.Provider>
    );
};

// Custom hook for using http context
export const useHttp = () => useContext(HttpRequestContext);

export default HttpRequestProvider;
